import Alert from '@xo-union/tk-component-alerts';
import { Formik, FormikHelpers } from 'formik';
import gql from 'graphql-tag';
import React, { useCallback, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { PagesFragmentDoc, PagesQuery, useCreatePageMutation } from '../../generated/graphql';
import {
  PAGE_NAMED_HOME_ERROR,
  PAGE_NAMED_RSVP_ERROR,
  PAGE_TAKEN_ERROR,
  PAGE_TITLE_BLANK_ERROR,
  STANDARD_ERROR_MESSAGE,
} from '../../helpers/errorMessage';
import useIsLiteSite from '../../helpers/useIsLiteSite';
import { PageCardUIContext } from '../../PageCardUIContext';
import { TrackingContext } from '../../TrackingContext';
import { pagesQuery } from '../LeftContainer/graphql';
import { EditPage } from '../PageSettingsRail/EditPage';

const validationSchema = Yup.object().shape({
  title: Yup.string().required(PAGE_TITLE_BLANK_ERROR),
  show: Yup.boolean(),
  note: Yup.string(),
});

export const createPageMutation = gql`
  mutation createPageMutation($attributes: WWS_PageAttributes!) {
    createPage(attributes: $attributes) {
      ...Pages
    }
  }
  ${PagesFragmentDoc}
`;

interface InitialValues {
  title: string;
  show: boolean;
}

interface Props {
  header?: string;
  onClose?: () => void;
  onSaveSuccess?: () => void;
  leftButtonClick?: () => void;
}

export const AddPage: React.FC<Props> = ({ leftButtonClick, onSaveSuccess, header = 'Add a Page', onClose }) => {
  const [createPage, { loading }] = useCreatePageMutation();
  const { togglePageOpen } = useContext(PageCardUIContext);
  const { track } = useContext(TrackingContext);
  const history = useHistory();
  const isLiteSite = useIsLiteSite();

  const alertRef = useRef<any>(null);
  const showAndDismissAlert = useCallback((message: string) => {
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message,
      waitTime: 3000,
    });
  }, []);

  const initialValues: InitialValues = {
    title: '',
    show: true,
  };

  const goBack = leftButtonClick || (() => history.goBack());
  const onSave = async (values: InitialValues, { setErrors }: FormikHelpers<InitialValues>) => {
    try {
      await createPage({
        variables: {
          attributes: {
            title: values.title,
            show: values.show,
          },
        },
        update(cache, { data }) {
          const { pages } = cache.readQuery<PagesQuery>({ query: pagesQuery })!;
          track('WWS Page Added', {
            action: 'add',
            pageTitle: data?.createPage?.title,
            pageVisible: data?.createPage?.show,
          });
          cache.writeQuery({
            query: pagesQuery,
            data: { pages: [...pages, data?.createPage] },
          });

          if (data?.createPage.id && togglePageOpen) {
            togglePageOpen(data.createPage.id);
          }
        },
      });
      if (onSaveSuccess) onSaveSuccess();
      goBack();
    } catch (err) {
      if (err.message.match(/Title has already been taken/i)) {
        setErrors({ title: PAGE_TAKEN_ERROR });
        showAndDismissAlert(PAGE_TAKEN_ERROR);
      } else if (err.message.match(/Custom page cannot be home/i)) {
        setErrors({ title: PAGE_NAMED_HOME_ERROR });
        showAndDismissAlert(PAGE_NAMED_HOME_ERROR);
      } else if (err.message.match(/Custom page title cannot be rsvp/i)) {
        setErrors({ title: PAGE_NAMED_RSVP_ERROR });
        showAndDismissAlert(PAGE_NAMED_RSVP_ERROR);
      } else {
        showAndDismissAlert(STANDARD_ERROR_MESSAGE);
      }
    }
  };

  return (
    <>
      <Formik enableReinitialize validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSave}>
        {({ submitForm, setFieldValue, dirty }): JSX.Element => (
          <EditPage
            onClose={onClose || goBack}
            submitForm={submitForm}
            setFieldValue={setFieldValue}
            isFormDirty={dirty}
            modules={[]}
            isLoading={loading}
            header={header}
            leftButtonOnClick={goBack}
            leftButtonText="Back"
            showVisibilityToggle={!isLiteSite}
            noClose={!onClose}
          />
        )}
      </Formik>
      <Alert ref={alertRef} position="bottom" />
    </>
  );
};
